import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import { canManageCreditPercentages, getPermissionErrorMessage } from '../services/staffPermissionService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';

interface CreditRuleData {
  minAmount: number;
  percentage: number;
  id?: string;
}

interface CreditRuleInputState {
  minAmount: string;
  percentage: string;
  id: string;
}

interface Business {
  id: string;
  name: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;
  @media (max-width: 768px) {
    max-width: 100%;
    // Allow horizontal scroll on very small screens if needed
    overflow-x: auto;
    // Hide scrollbar visually but allow scrolling
    &::-webkit-scrollbar {
       display: none; // Hide scrollbar for Webkit browsers
    }
    -ms-overflow-style: none;  // Hide scrollbar for IE and Edge
    scrollbar-width: none;  // Hide scrollbar for Firefox
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px; // Keep this for desktop

  @media (max-width: 768px) { // Adjusted breakpoint for consistency
    max-width: 80px;
  }

  @media (max-width: 480px) {
    max-width: 60px; // Further reduce on very small screens
  }
`;

const BusinessSection = styled.div`
  margin-bottom: 40px;
  width: 100%;
`;

const RuleCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const RuleCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  width: 100%;
`;

const RuleFormattedInput = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap; // Allow wrapping on small screens
  gap: 8px;

  @media (min-width: 768px) {
    flex-wrap: nowrap; // Prevent wrapping on larger screens
  }
`;

const InputContainer = styled.div`
  width: 120px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100px;
  }
  @media (max-width: 480px) {
     width: 80px;
  }
`;

const InputText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  line-height: 1.5;
  @media (max-width: 768px) { // Adjusted breakpoint
    font-size: 14px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
`;

const SuccessMessage = styled.p`
  color: #43a047;
  margin-top: 16px;
  font-size: 14px;
`;

const ErrorMessage = styled.p`
  color: #e53935;
  margin-top: 16px;
  font-size: 14px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  font-size: 16px;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  font-family: 'Montserrat', sans-serif;
  outline: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  color: #333;
  text-align: center;
  &[type=number]::-webkit-inner-spin-button,
  &[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const SentenceInput: React.FC<{
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  min?: string;
  max?: string;
  step?: string;
}> = ({ type, value, onChange, placeholder, min, max, step }) => {
  return (
    <InputContainer>
      <StyledInput
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
      />
    </InputContainer>
  );
};


const BusinessCreditPercentagePage: React.FC = () => {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [permissionError, setPermissionError] = useState<string>('');

  const [creditRules, setCreditRules] = useState<CreditRuleInputState[]>(() =>
    Array.from({ length: 4 }, (_, i) => ({
      minAmount: '',
      percentage: '',
      id: `rule-init-${Date.now() + i}`
    }))
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchUserAndBusinessData = async () => {
      if (!businessId) return;

      try {
        setLoading(true);
        const userData = await getCurrentUser();
        setUser(userData);

        if (!userData?.id) {
          navigate('/login');
          return;
        }

        const hasPermission = await canManageCreditPercentages(userData.id, businessId);
        if (!hasPermission) {
          setPermissionError(getPermissionErrorMessage('credit-percentages'));
          setLoading(false);
          return;
        }

        const businessDoc = await getDoc(doc(db, 'businesses', businessId));

        if (businessDoc.exists()) {
          setBusiness({
            id: businessDoc.id,
            name: businessDoc.data().name
          });
          await fetchCreditRules(businessId);
        } else {
          console.error("Business not found");
          navigate('/businesses');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load business data.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndBusinessData();
  }, [businessId, navigate]);

  const fetchCreditRules = async (businessId: string) => {
    try {
      const creditDocRef = doc(db, 'creditPercentages', businessId);
      const creditDoc = await getDoc(creditDocRef);

      let rulesToSet: CreditRuleInputState[] = [];

      if (creditDoc.exists() && creditDoc.data()?.rules) {
        const existingRules = creditDoc.data()?.rules as CreditRuleData[]; 
        rulesToSet = existingRules.map((rule, index) => ({
          minAmount: String(rule.minAmount || 0),
          percentage: String(rule.percentage || 0),
          id: rule.id || `rule-fetched-${Date.now() + index}`
        }));
      } else {
        rulesToSet = [
          { minAmount: '0', percentage: '5', id: `rule-default-${Date.now()}-0` }
        ];
      }

      while (rulesToSet.length < 4) {
        rulesToSet.push({
          minAmount: '',
          percentage: '',
          id: `rule-padded-${Date.now() + rulesToSet.length}`
        });
      }

      if (rulesToSet.length > 4) {
        rulesToSet = rulesToSet.slice(0, 4);
      }

      setCreditRules(rulesToSet);

    } catch (error) {
      console.error('Error fetching credit rules:', error);
      setError('Failed to load credit rules.');
    }
  };

   const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };
   const handleNavigateToMyBusinesses = () => {
    navigate('/businesses');
  };
   const handleNavigateToSettings = () => {
    navigate(`/business/${businessId}/settings`);
  };

  const handleRuleChange = (index: number, field: 'minAmount' | 'percentage', value: string) => {
    setCreditRules(prevRules => {
      const newRules = [...prevRules];
      const validValue = /^[0-9]*\.?[0-9]*$/.test(value) ? value : prevRules[index][field];

      newRules[index] = {
        ...newRules[index],
        [field]: validValue
      };
      return newRules;
    });
    setError('');
    setSuccess('');
  };

  // --- Modified validateRules ---
  const validateRules = (): boolean => {
    // Convert string state values to numbers for validation
    const rulesToValidate = creditRules.map(rule => ({
      id: rule.id, // Keep id for potential error referencing
      minAmount: parseFloat(rule.minAmount || '0'), // Parse string to float, default 0 if empty/invalid
      percentage: parseFloat(rule.percentage || '0') // Parse string to float, default 0 if empty/invalid
    }));

    // Filter out rules where both minAmount and percentage are effectively 0
    const activeRules = rulesToValidate.filter(rule => rule.minAmount > 0 || rule.percentage > 0);

    if (activeRules.length === 0) {
      return true; // Allow saving if all rules are zeroed out
    }

    const sortedRules = [...activeRules].sort((a, b) => a.minAmount - b.minAmount);

    for (let i = 1; i < sortedRules.length; i++) {
      if (sortedRules[i].minAmount > 0 && sortedRules[i-1].minAmount > 0 && sortedRules[i].minAmount <= sortedRules[i-1].minAmount) {
        setError(`Rules have overlapping spending thresholds. Each minimum amount ($${sortedRules[i].minAmount}) must be greater than the previous non-zero amount ($${sortedRules[i-1].minAmount}).`);
        return false;
      }
    }

    for (const rule of sortedRules) {
      if (rule.percentage < 0 || rule.percentage > 100) {
        setError(`percentage (${rule.percentage}%) for the $${rule.minAmount} threshold must be between 0% and 100%`);
        return false;
      }
       // Optional: Check if minAmount is 0 but percentage is not (if this is invalid)
      // if (rule.minAmount === 0 && rule.percentage > 0) {
      //    setError('A rule with $0 minimum spend cannot have a percentage greater than 0.');
      //    return false;
      // }
    }

    return true;
  };

  // --- Modified handleSaveRules ---
  const handleSaveRules = async () => {
    setError('');
    setSuccess('');

    if (!businessId) return;

    // Validate using the current state (validateRules handles parsing)
    if (!validateRules()) {
      return;
    }

    try {
      setSaving(true);

      // Convert state strings to numbers for saving
       const rulesToSaveToDb = creditRules
        .map(rule => ({
          // No need to map ID if Firestore generates it or it's not part of the rule data itself
          minAmount: parseFloat(rule.minAmount || '0'),
          percentage: parseFloat(rule.percentage || '0')
        }))
        .filter(rule => rule.minAmount > 0 || rule.percentage > 0) // Filter out zeroed rules
        .sort((a, b) => a.minAmount - b.minAmount); // Sort before saving


      // Save the numerically converted, filtered, and sorted rules
      await setDoc(doc(db, 'creditPercentages', businessId), {
        businessId,
        rules: rulesToSaveToDb, // Save the processed CreditRuleData structure
        updatedAt: new Date()
      }, { merge: true }); // Use merge: true to avoid overwriting other potential fields

      // Fetch rules again to ensure UI reflects exactly what's saved (handles sorting/filtering)
      await fetchCreditRules(businessId);
      setSuccess('Credit rules saved successfully');

    } catch (error) {
      console.error('Error saving credit rules:', error);
      setError('Failed to save credit rules. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // --- Loading State (Keep as is) ---
  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          <div>Loading...</div>
        </ContentWrapper>
      </Container>
    );  }

  // --- JSX Rendering (Keep as is, SentenceInput value is now correctly string) ---
  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Credit Percentages</title>
        <meta name="description" content={`${APP_NAME} - Credit Percentages`} />
      </Helmet>
      <Container>
        <ContentWrapper>
          <TopBar user={user} />

          <PageTitle>Percentages</PageTitle>

        <Breadcrumbs>
           <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
           <BreadcrumbSeparator>/</BreadcrumbSeparator>
           <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>Businesses</BreadcrumbLink>
           <BreadcrumbSeparator>/</BreadcrumbSeparator>
           <BreadcrumbLink onClick={handleNavigateToSettings}>
             <BreadcrumbText title={business?.name}>{business?.name || 'Business Settings'}</BreadcrumbText>
           </BreadcrumbLink>
           <BreadcrumbSeparator>/</BreadcrumbSeparator>
           <span>Percentages</span>
        </Breadcrumbs>

        <BusinessSection>
          <RuleCardList>
            {creditRules.map((rule, index) => (
              <RuleCard key={rule.id}> {/* Use rule.id from state */}
                <RuleFormattedInput>
                  <InputText>{isMobile ? 'Spend' : 'If customer spends'} $</InputText>
                  <SentenceInput
                    type="number" // Keep type="number" for keyboard hints, but value is string
                    value={rule.minAmount} // Directly use string value from state
                    onChange={(e) => handleRuleChange(index, 'minAmount', e.target.value)}
                    placeholder="$0.00" // More specific placeholder
                    min="0"
                    step="0.01"
                  />
                  <InputText>{isMobile ? 'Earn' : 'give'}</InputText>
                  <SentenceInput
                    type="number" // Keep type="number"
                    value={rule.percentage} // Directly use string value from state
                    onChange={(e) => handleRuleChange(index, 'percentage', e.target.value)}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <InputText>{isMobile ? '%' : '% store credit'}</InputText>
                </RuleFormattedInput>
              </RuleCard>
            ))}
          </RuleCardList>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          {permissionError && <ErrorMessage>{permissionError}</ErrorMessage>}

          <ButtonContainer>
            <Button
              text="Save"
              onClick={handleSaveRules}
              loading={saving}
              bgColor="#000"
              textColor="#fff"
              width="auto"
              minWidth="150px"            />
          </ButtonContainer>
        </BusinessSection>
      </ContentWrapper>
    </Container>
    </>
  );
};

export default BusinessCreditPercentagePage;